import React from 'react';
import styled from 'styled-components';

import type { RewardsActivity } from 'src/gql/graphql';
import { formatTransactionDate, formatLoyaltyExpirationDate } from './dpos-utils';

type RewardsActivityProps = {
  rewardsActivity: Array<RewardsActivity>;
  isPoints: boolean;
  formatValueForDisplay: (value: number, label: string, isLoyaltyPoints: boolean) => string;
};

export const DposRewardsActivityList = ({
  isPoints = false,
  rewardsActivity = [],
  formatValueForDisplay,
}: RewardsActivityProps): JSX.Element => (
  <RewardsList>
    {rewardsActivity.length > 0 ? (
      rewardsActivity.map(
        ({ transactionType, orderType, transactionDateUtc, location, points, totalPaid, expirationDate }) => {
          const isExpired = expirationDate ? new Date(expirationDate) < new Date() : false;
          const showExpiration = !!expirationDate;

          return (
            <Reward key={transactionDateUtc} $points={points}>
              <DescriptionBlock>
                <Description>
                  {transactionType}
                  <OrderType> - {orderType}</OrderType>
                </Description>
                <TransactionDate>
                  {formatTransactionDate(transactionDateUtc ?? '')} {location ? `- ${location}` : null}
                </TransactionDate>
                {totalPaid ? <TotalPaidMobile>{formatValueForDisplay(totalPaid, '', false)}</TotalPaidMobile> : null}
              </DescriptionBlock>
              <CostInPointsOrCashMobile $points={points} $expired={isExpired}>
                {points && points > 0 ? '+' : null}
                {formatValueForDisplay(points ?? 0, 'pts', isPoints)}
                {showExpiration && (
                  <ExpirationPill $expired={isExpired}>
                    {isExpired ? 'Expired' : 'Expires'} {formatLoyaltyExpirationDate(expirationDate)}
                  </ExpirationPill>
                )}
              </CostInPointsOrCashMobile>

              {totalPaid ? <TotalPaid>{formatValueForDisplay(totalPaid, '', false)}</TotalPaid> : null}
              <CostInPointsOrCash $points={points} $expired={isExpired}>
                {points && points > 0 ? '+' : null}
                {formatValueForDisplay(points ?? 0, 'pts', isPoints)}
                {showExpiration && (
                  <ExpirationPill $expired={isExpired}>
                    {isExpired ? 'Expired' : 'Expires'} {formatLoyaltyExpirationDate(expirationDate)}
                  </ExpirationPill>
                )}
              </CostInPointsOrCash>
            </Reward>
          );
        }
      )
    ) : (
      <NoActivityAvailable>No rewards activity at this time</NoActivityAvailable>
    )}
  </RewardsList>
);

const RewardsList = styled.dl`
  list-style: none;
  margin-top: 0;
`;

const Description = styled.dt`
  font-weight: bold;
`;

const OrderType = styled.span`
  font-weight: normal;
`;

const DescriptionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 45%;
`;

const CostInPointsOrCashMobile = styled.dd<{ $points?: number | null; $expired?: boolean }>`
  color: ${({ $points, $expired, theme }) => {
    if ($expired) return '#828A8F';
    return ($points ?? 0) > 0 ? theme.colors.primaryGreen : theme.colors.red[60];
  }};
  display: none;
  text-align: right;
`;

const TotalPaidMobile = styled.dd`
  display: none;
`;

const CostInPointsOrCash = styled.dd<{ $points?: number | null; $expired?: boolean }>`
  color: ${({ $points, $expired, theme }) => {
    if ($expired) return '#828A8F';
    return ($points ?? 0) > 0 ? theme.colors.primaryGreen : theme.colors.red[60];
  }};
  margin: 0;
  align-content: center;
  font-weight: bold;
  font-size: 13px;
  text-align: right;
`;

const TotalPaid = styled.dd`
  margin: 0;
  align-content: center;
  font-weight: normal;
  font-size: 13px;
`;

const TransactionDate = styled.dd`
  color: #828a8f;
  margin: 0;
  font-size: 13px;
`;

const NoActivityAvailable = styled.div`
  color: #828a8f;
`;

const ExpirationPill = styled.span<{ $expired: boolean }>`
  display: flex;
  max-width: 200px;
  padding: 4px;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
  margin-left: auto;
  font-size: 10px;
  font-weight: 400;
  color: ${({ $expired }) => ($expired ? '#828A8F' : '#2C3236')};
  background: ${({ $expired }) => ($expired ? '#F5F6F7' : '#E3E7E9')};
  border-radius: 4px;
`;

const Reward = styled.div<{ $points: string }>`
  margin-top: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  display: flex;
  gap: 50px;
  padding: 20px 0;
  justify-content: space-between;

  @media only screen and (min-width: 100px) and (max-width: 599px) {
    padding: 10px 0;

    ${DescriptionBlock} {
      display: flex;
      width: 65%;
    }
    ${CostInPointsOrCashMobile} {
      display: block;
      justify-content: end;
      margin: 0;
      align-content: center;
      font-weight: bold;
      font-size: 13px;
    }
    ${CostInPointsOrCash} {
      display: none;
    }
    ${TotalPaid} {
      display: none;
    }
    ${TotalPaidMobile} {
      margin: 0;
      align-content: center;
      font-weight: normal;
      font-size: 13px;
      display: flex;
      margin-top: 8px;
    }
  }
`;
